<template>
	<div class="video-download">
		<Modal
			:modal-name="modalName"
			class="video-download__modal"
			type="dialog"
			:theme="$arenaConfig.isLightTheme ? null : 'dark'"
			@opened="onOpen"
		>
			<template #title>
				{{ $I18n.trans_choice('commons.video', 1) + ' ' + $I18n.trans('commons.download') }}
			</template>
			<template #content>
				<LoadingSpinner
					v-if="isLoadingParts && !needsPassword"
					class="video-download__loading-spinner"
					size="md"
				/>
				<template v-else>
					<div
						class="video-download__cover-w"
						role="region"
					>
						<div
							class="video-download__cover-image"
							:style="`backgroundImage: url('${backgroundImageUrl}')`"
						/>
						<div
							v-if="videoParts.length"
							class="video-download__duration-badge"
						>
							<icon
								icon-name="button-play-filled"
								vertical-align="baseline"
								size="xs"
							/>
							{{ durationString }}
						</div>
						<div
							v-else
							class="video-download__cover-message"
							:class="{'has-background-image': backgroundImageUrl}"
						>
							{{ cardMessage }}
						</div>
					</div>
					<InputText
						type="text"
						name="description"
						class="video-download__description"
						:disabled="!selectedVideoPartId"
						:label="$I18n.trans('commons.description')"
						:value="description"
						maxlength="50"
						:dark-theme="!$arenaConfig.isLightTheme"
						@input="description = $event"
					/>
					<Select
						v-if="showSizeSelection && videoParts.length"
						v-model="selectedVideoPartId"
						:disabled="isLoadingParts"
						:clearable="false"
						:searchable="false"
						name="selectedVideoPartId"
						:label="$I18n.trans('commons.quality')"
						:options="videoParts"
						:dark-theme="!$arenaConfig.isLightTheme"
						:text-after="selectedVideoPart ? formatBytes(selectedVideoPart.size) : null"
						class="video-download__select"
					/>
					<InputText
						v-if="needsPassword"
						type="password"
						name="downloadPassword"
						:required="true"
						:disabled="videoParts.length > 0"
						:placeholder="`${$I18n.trans('commons.password')}...`"
						:label="$I18n.trans('commons.password')"
						:value="password"
						maxlength="50"
						:dark-theme="!$arenaConfig.isLightTheme"
						@input="(value) => password = value"
					/>
				</template>
			</template>
			<template #footer>
				<div class="l-button-group">
					<V2Button
						:text="$Helpers.ucfirst($I18n.trans('commons.cancel'))"
						modifier="secondary"
						theme="dark"
						@click="closeModal"
					/>
					<V2Button
						:disabled="!selectedVideoPartId"
						icon="cloud-download"
						:text="$Helpers.ucfirst($I18n.trans('commons.download'))"
						theme="dark"
						@click="download"
					/>
				</div>
			</template>
		</Modal>

		<button
			type="button"
			class="video-download__button"
			:aria-label="`${$I18n.trans('commons.download_video')}: ${event.name}`"
			@click.stop="handleDownloadClick"
		>
			<Icon
				icon-name="data-download"
				size="lg"
			/>
		</button>
	</div>
</template>

<script>
import V2Button from '@shared/components/Button';
import Modal from '@shared/components/Modal';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import Select from '@shared/components/Select';
import InputText from '@shared/components/Form/InputText';
import Icon from '@shared/components/Icon';
import { httpClient } from '@shared/js/api/httpClient';
import moment from 'moment';
import { EventAccessRestriction } from '@shared/enums/EventAccessRestriction';
import { formatBytes } from '@shared/utils/formatBytes';
import TrackingService from '@shared/js/TrackingService';
import { AnalyticsEvent } from '@shared/enums/AnalyticsEvent';

export default {
	name:       'VideoDownload',
	components: {
		Modal,
		Select,
		InputText,
		V2Button,
		LoadingSpinner,
		Icon,
	},
	props: {
		event: {
			type:     Object,
			required: true,
		},
		showSizeSelection: {
			type:    Boolean,
			default: false,
		},
		loginRestricted: {
			type:    Boolean,
			default: false,
		},
		isLoggedIn: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			modalName:           'VideoDownload',
			isLoadingParts:      false,
			selectedVideoPartId: null,
			description:         this.event.name,
			needsPassword:       false,
			password:            null,
			videoParts:          [],
			formatBytes,
		};
	},
	computed: {
		backgroundImageUrl() {
			return this.event?.imageUrl ? this.$Helpers.getScaledImageUrl(this.event.imageUrl, 360) : null;
		},
		isEnded() {
			return this.event?.isEnded ?? false;
		},
		selectedVideoPart() {
			return this.selectedVideoPartId ? this.videoParts.find(part => part.value === this.selectedVideoPartId) : null;
		},
		cardMessage() {
			return this.needsPassword ? this.$I18n.trans('event.download.invalid_password') : this.$I18n.trans('event.download.event_upcoming');
		},
		durationString() {
			if (this.event) {
				const durationSeconds = this.event.durationSeconds;
				return moment.duration({ seconds: durationSeconds }).format(`${durationSeconds >= 3600 ? 'H:' : ''}mm:ss`, {
					trim: false,
				});
			} else {
				return '';
			}
		},
		filename() {
			return this.description && this.description.length > 0 && this.selectedVideoPart
				? [
					moment(this.start).format('YYYY_MM_DD'), this.$Helpers.slugify(this.description), `${this.selectedVideoPart.height}p`,
				].filter(value => !!value).join('_')
				: null;
		},
	},
	watch: {
		password() {
			this.getVideoParts();
		},
	},
	methods: {
		handleDownloadClick() {
			if (this.loginRestricted && !this.isLoggedIn) {
				this.eventBus.$emit('modal:LoginWallModal', {
					title:    this.$I18n.trans('commons.download_video'),
					text:     this.$I18n.trans('arena.feature_access.login_wall.text_feature'),
					closable: true,
				});
			} else {
				this.openModal();
			}
		},
		openModal() {
			window.eventBus.$emit(`modal:${this.modalName}`);
		},
		closeModal() {
			window.eventBus.$emit(`modalClose:${this.modalName}`);
		},
		onOpen() {
			this.getVideoParts();
		},
		download() {
			if (this.selectedVideoPart && this.selectedVideoPart.downloadUrl) {
				const link = document.createElement('a');
				link.href = this.selectedVideoPart.downloadUrl;
				link.click();
				TrackingService.trackEvent(this.event.id, AnalyticsEvent.EVENT_DOWNLOAD);
			}
		},
		async getVideoParts() {
			this.isLoadingParts = true;

			try {
				const params = {
					includeDownloadUrl: true,
					domainKey:          this.$arenaConfig.domainKey,
				};

				if (this.password) {
					params.downloadPassword = this.password;
				}

				const response = await httpClient.getEventVideoParts(this.event?.id, params);

				this.meta = response.data?.meta ?? null;
				this.mapVideoParts(response.data?.data ?? []);
			} catch (error) {
				if (error.response?.data?.error && error.response?.data?.reason === EventAccessRestriction.PASSWORD) {
					this.needsPassword = true;
				} else {
					window.toast.error(this.$I18n.trans('errors.500.title'));
				}
			} finally {
				this.isLoadingParts = false;
			}
		},
		mapVideoParts(parts) {
			this.videoParts = parts.filter(part => part.label.toLowerCase() !== 'source').map((part, i) => ({ ...part, value: i + 1 }));
			this.selectedVideoPartId = this.videoParts[0].value;
		},

	},
};
</script>

<style lang="scss">
@import '@shared/sass/shared-variables';

.video-download {
	align-self: flex-start;
}

.video-download__modal {
	.v2-modal__content-w {
		overflow: visible !important; // stylelint-disable-line declaration-no-important
	}

	.v2-modal__content {
		overflow: visible;
		display: flex;
		flex-direction: column;
		gap: $sp5;
	}

	.v2-modal__footer {
		z-index: 0;
	}
}

.video-download__loading-spinner {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
}

.video-download__cover-w {
	aspect-ratio: 16/9;
	position: relative;
}

.video-download__cover-image {
	background-size: cover;
	width: 100%;
	height: 100%;
}

.video-download__description {
	margin-bottom: 0 !important; // stylelint-disable-line declaration-no-important
}

.video-download__duration-badge {
	position: absolute;
	right: $sp4;
	bottom: $sp4;
	display: flex;
	align-items: center;
	gap: 4px;
	color: var(--color-soft-white);
	min-height: 21px; // stylelint-disable-line
	background-color: rgba(14, 15, 15, .52); // stylelint-disable-line
	line-height: 20px; // stylelint-disable-line
	font-size: 14px; // stylelint-disable-line
	padding: 2px 10px; // stylelint-disable-line
	text-transform: uppercase;
}

.video-download__cover-message {
	@include font(primary, book, normal, fs-80);

	text-align: start;
	padding: $sp5;
	display: flex;
	justify-content: flex-start;
	align-items: flex-end;
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: var(--color-shadow-grey);
	color: var(--color-soft-white);

	&.has-background-image {
		opacity: 0.8;
	}
}

.video-download__button {
	width: $sp5;
	height: $sp5;
	cursor: pointer;
}

.video-download__select {
	margin-bottom: 0;
}
</style>
